import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  Users, 
  CheckCircle, 
  Ticket, 
  AlertCircle,
  Loader2,
  Phone,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QueuePublic = () => {
  const { queueId } = useParams();
  const [queueInfo, setQueueInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: ''
  });

  useEffect(() => {
    fetchQueueInfo();
  }, [queueId]);

  const fetchQueueInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/queues/${queueId}/public-info`);
      if (!response.ok) throw new Error('File non trouvée');
      const data = await response.json();
      setQueueInfo(data);
    } catch (error) {
      toast.error('Impossible de charger les informations de la file');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeTicket = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/queues/${queueId}/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: form.name,
          patient_phone: form.phone || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la prise de ticket');
      }

      const data = await response.json();
      setTicket(data.ticket);
      toast.success('Ticket obtenu !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  if (!queueInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">File non trouvée</h2>
            <p className="text-slate-600">Cette file d&apos;attente n&apos;existe pas ou a été supprimée.</p>
            <Link to="/">
              <Button className="mt-4">Retour à l&apos;accueil</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user already has a ticket
  if (ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2 border-green-200">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">Ticket obtenu !</CardTitle>
            <CardDescription>
              {queueInfo.facility_name}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            {/* Ticket Number */}
            <div className="bg-gradient-to-r from-[#1A237E] to-[#3949AB] rounded-2xl p-6 text-white">
              <p className="text-sm opacity-80 mb-2">Votre numéro</p>
              <p className="text-6xl font-bold">{ticket.ticket_number}</p>
              <p className="text-sm opacity-80 mt-2">{queueInfo.name}</p>
            </div>

            {/* Status Info */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-slate-50 rounded-xl p-4">
                <Users className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-slate-900">{ticket.position}</p>
                <p className="text-xs text-slate-500">Devant vous</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <Clock className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-slate-900">~{ticket.estimated_wait_minutes}</p>
                <p className="text-xs text-slate-500">min d&apos;attente</p>
              </div>
            </div>

            {/* Current serving */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <p className="text-sm text-amber-700">Numéro actuellement servi</p>
              <p className="text-3xl font-bold text-amber-800">{queueInfo.now_serving || '-'}</p>
            </div>

            {ticket.patient_phone && (
              <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" />
                Vous serez notifié au {ticket.patient_phone}
              </p>
            )}

            <div className="pt-4 border-t">
              <p className="text-xs text-slate-400">
                Gardez cette page ouverte pour suivre votre position
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Queue is closed
  if (!queueInfo.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">File fermée</h2>
            <p className="text-slate-600 mb-2">{queueInfo.name}</p>
            <p className="text-sm text-slate-500">
              Cette file d&apos;attente est actuellement fermée. Veuillez réessayer plus tard.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form to take ticket
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#FFEBEE] rounded-full flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-8 h-8 text-[#DC2626]" />
          </div>
          <CardTitle className="text-xl">Prendre un ticket</CardTitle>
          <CardDescription className="space-y-1">
            <span className="flex items-center justify-center gap-2">
              <Building2 className="w-4 h-4" />
              {queueInfo.facility_name}
            </span>
            <Badge variant="outline" className="mt-2">{queueInfo.name}</Badge>
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Current status */}
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-[#1A237E]">{queueInfo.waiting_count}</p>
              <p className="text-xs text-slate-500">En attente</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-[#1A237E]">~{queueInfo.estimated_wait_minutes}</p>
              <p className="text-xs text-slate-500">min d&apos;attente</p>
            </div>
          </div>

          {queueInfo.now_serving > 0 && (
            <div className="text-center bg-green-50 rounded-xl p-3 border border-green-200">
              <p className="text-xs text-green-600">Numéro actuellement servi</p>
              <p className="text-2xl font-bold text-green-700">{queueInfo.now_serving}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleTakeTicket} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Votre nom *</Label>
              <Input
                id="name"
                placeholder="Entrez votre nom"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone (optionnel)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Ex: 6XX XX XX XX"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                Pour recevoir une notification quand c&apos;est votre tour
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] text-lg"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Ticket className="w-5 h-5 mr-2" />
                  Obtenir mon ticket
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-slate-400">
            Propulsé par DOCKHTA - Votre santé, notre priorité
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default QueuePublic;
